// The share credential a signed-out visitor is currently holding.
//
// /restaurant/:id renders for guests who arrived from a shared poll or a shared
// template (ProtectedRoute allowAnonymous), and /shared-cart/:cartId renders for
// guests holding a cart link. Those pages read the provider menu, and the API
// requires signed-out callers to prove they were invited — so the credential has
// to ride along on the request.
//
// It is read from the current URL rather than threaded through props because
// every one of those reads happens several layers below the page that owns the
// token (page -> useProviderMenu -> menuProviderService -> provider -> fetch),
// and the guest is, by construction, ON the URL that carries it.
//
// This grants nothing by itself: the server verifies the value against the
// share that minted it. A value that does not resolve is refused.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const asUuid = (value) => {
  const text = typeof value === 'string' ? value.trim() : '';
  return UUID_RE.test(text) ? text : null;
};

/**
 * @returns {{ key: string, value: string } | null} the credential to send, or
 * null when this page carries none (a signed-in user needs no credential).
 */
export function readShareCredential(location = typeof window !== 'undefined' ? window.location : null) {
  if (!location) return null;
  try {
    const params = new URLSearchParams(location.search || '');
    const poll = asUuid(params.get('sharedPollToken'));
    if (poll) return { key: 'sharedPollToken', value: poll };
    const template = asUuid(params.get('sharedTemplateToken'));
    if (template) return { key: 'sharedTemplateToken', value: template };
    // /shared-cart/<cartId> — the cart id IS the capability the link hands out.
    const cart = asUuid((location.pathname || '').match(/^\/shared-cart\/([^/?#]+)/)?.[1]);
    if (cart) return { key: 'sharedCartId', value: cart };
    return null;
  } catch {
    return null;
  }
}

// A signed-out visitor who arrived from a shared poll or template holds that
// share's token in the URL. `restaurants` and `menu_items` are readable only by
// signed-in users, so for them a restaurant and its menu come through
// get_shared_restaurant (migration 074), which answers only when that share
// points at the restaurant.
const SHARE_KIND_BY_PARAM = { sharedPollToken: 'poll', sharedTemplateToken: 'template' };

/**
 * @returns {Promise<{ kind: 'poll' | 'template', token: string } | null>} the
 * poll/template share a signed-out visitor holds, or null for everyone else
 * (signed-in users, shared-cart guests, pages with no share) so their reads stay
 * on the normal path.
 */
export async function guestShareCredential(supabase) {
  const credential = readShareCredential();
  const kind = credential ? SHARE_KIND_BY_PARAM[credential.key] : null;
  if (!kind) return null;
  try {
    const { data } = await supabase.auth.getSession();
    if (data?.session?.access_token) return null;
  } catch {
    // Unreadable session: treat as signed out.
  }
  return { kind, token: credential.value };
}

/** Append the current share credential to `path`, if there is one. */
export function withShareCredential(path) {
  const credential = readShareCredential();
  if (!credential) return path;
  const [base, hash = ''] = String(path).split('#');
  const separator = base.includes('?') ? '&' : '?';
  const suffix = `${credential.key}=${encodeURIComponent(credential.value)}`;
  return `${base}${separator}${suffix}${hash ? `#${hash}` : ''}`;
}
